import React, { useState, useEffect, useCallback, useContext } from 'react';
import styled from 'styled-components';
import type { CustomFactorForecast, InvestmentForecast, CMAGrouping } from 'venn-api';
import { getFactorCMAs } from 'venn-api';
import { Link as LinkStyle, Icon, Body1, Headline3, getHelpCenterLinks } from 'venn-ui-kit';
import { useApi } from 'venn-utils';
import { WithAnalysisViewId } from '../../../hooks/WithAnalysisViewId';
import CapitalMarketAssumptionsEditorV2 from '../components/CapitalMarketAssumptionsEditorV2';
import { ForecastPanelActionsContext } from '../contexts/ForecastPanelActionsContext';

interface CapitalMarketAssumptionsViewV2Props {
  forecast?: CustomFactorForecast;
  onClose: () => void;
  analysisViewId: string | undefined;
}

const CapitalMarketAssumptionsViewV2Inner = ({
  forecast,
  onClose,
  analysisViewId,
}: CapitalMarketAssumptionsViewV2Props) => {
  const isEditing = forecast !== undefined;
  const [cmaGroups, setCMAGroups] = useState<CMAGrouping[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const abortableGetFactorCMAs = useApi(getFactorCMAs);
  const { onUpdateOrCreateFactorForecast } = useContext(ForecastPanelActionsContext);
  useEffect(() => {
    const onMount = async () => {
      setLoading(true);
      try {
        const groups = (await abortableGetFactorCMAs('APP', analysisViewId)).content;
        setCMAGroups(groups);
        setLoading(false);
      } catch (e) {
        // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
        if (e.name === 'AbortError') {
          return;
        }
        setCMAGroups(null);
        setLoading(false);
      }
    };
    onMount();
  }, [abortableGetFactorCMAs, analysisViewId]);

  const onSubmit = useCallback(
    (assumptions: InvestmentForecast[], name: string, editedForecast?: CustomFactorForecast) => {
      !editedForecast
        ? onUpdateOrCreateFactorForecast(
            {
              name,
              investmentForecasts: assumptions,
            } as CustomFactorForecast,
            true,
          )
        : onUpdateOrCreateFactorForecast(
            {
              ...editedForecast,
              name,
              investmentForecasts: assumptions,
            },
            false,
          );
    },
    [onUpdateOrCreateFactorForecast],
  );

  return (
    <ViewContainer>
      <SubHeading>{isEditing ? 'Edit' : 'Enter'} Your Capital Market Assumptions</SubHeading>
      <Body1>Assumptions must be annualized averages. </Body1>
      <LinkBody>
        <LinkStyle>
          <a target="_blank" rel="noopener noreferrer" href={getHelpCenterLinks().FORECASTS_FAQ_HREF}>
            <Icon type="up-right-from-square" /> Learn more
          </a>
        </LinkStyle>
      </LinkBody>
      <CapitalMarketAssumptionsEditorV2
        forecast={forecast}
        cmaGroups={cmaGroups}
        isLoadingCMAGroups={loading}
        onCancel={onClose}
        onSubmitAssumptions={onSubmit}
      />
    </ViewContainer>
  );
};

const SubHeading = styled(Headline3)`
  margin-top: 18px;
`;

const LinkBody = styled(Body1)`
  margin-bottom: 20px;
`;

const ViewContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

// HOC wrapper that provides analysisViewId
export const CapitalMarketAssumptionsViewV2 = ({
  forecast,
  onClose,
}: Omit<CapitalMarketAssumptionsViewV2Props, 'analysisViewId'>) => (
  <WithAnalysisViewId>
    {(analysisViewId) => (
      <CapitalMarketAssumptionsViewV2Inner forecast={forecast} onClose={onClose} analysisViewId={analysisViewId} />
    )}
  </WithAnalysisViewId>
);
